import { useCallback, useMemo } from "react"
import { useParams } from "react-router-dom"
import { useApprovers } from "../../../../../../hooks/useApprovers"
import { useDeleteConfirm } from "../../../../../../hooks/useDeleteConfirm"
import { ApplicationRoleApproverGet } from "../../../../../models/ApplicationRoleApprover"
import { DimaFeatureShortNameEnum } from "../../../../../models/Authorization"
import { deleteEntity } from "../../../../../utils/deleteEntity"
import { booleanComparator, getDoneIcon, getEmptyIcon } from "../../../../../utils/getBooleanFlagIcon"
import { DiMaAddButton } from "../../../../partials/DiMaAddButton"
import { EntityRow, EntityTab } from "../../../../partials/EntityTab"

export function ApplicationRoleApprovers() {
  const { applicationId, roleId } = useParams()
  const { requestDelete, DeleteConfirmDialog } = useDeleteConfirm()
  const { approversList, isLoadingApprovers } = useApprovers(applicationId, roleId)

  const columns = [
    {
      field: "username",
      headerName: "Email address",
      isLink: true,
      linkBasePath: `/applications/${applicationId}/roles/${roleId}/approvers`,
    },
    { field: "fullName", headerName: "Full name" },
    {
      field: "hasProperties",
      headerName: "Has properties",
      renderCell: (row: EntityRow) => row.hasProperties ? getDoneIcon() : getEmptyIcon(),
      sortComparator: booleanComparator,
    },
  ]

  const sorted = [...approversList].sort(
    (a: ApplicationRoleApproverGet, b: ApplicationRoleApproverGet) =>
      (a.user?.username ?? "").localeCompare(b.user?.username ?? "")
  )

  const rows = sorted.map((approver: ApplicationRoleApproverGet) => ({
    id: approver.user.id,
    userId: approver.user.id,
    username: approver.user.username,
    fullName: `${approver.user.first ?? ""} ${approver.user.last ?? ""}`,
    hasProperties:
      approver.properties.length > 0 && approver.properties[0].name !== null,
  }))

  const toolbar = useMemo(
    () => (
      <DiMaAddButton
        url={`/applications/${applicationId}/roles/${roleId}/approvers/create`}
        applicationId={applicationId}
        feature={DimaFeatureShortNameEnum.AM}
      />
    ),
    [applicationId, roleId]
  )

  const handleActionMenuClick = useCallback((item: { title: string }, row: EntityRow) => {
      if (item.title === "Delete") {
        requestDelete(() =>
          deleteEntity(
            `/applications/${applicationId}/roles/${roleId}/approvers/${row.userId}`
          )
        )
      }
    }, [applicationId, roleId, requestDelete])

  return (
    <>
      {DeleteConfirmDialog}
      <EntityTab
        rows={rows}
        cols={columns}
        isLoading={isLoadingApprovers}
        toolbar={toolbar}
        actionItems={[{ title: "Delete", iconName: "icon_abb_dias icon-abb_delete" }]}
        onActionMenuClick={handleActionMenuClick}
      />
    </>
  )
}
